<script setup lang="ts">
  import { computed, getCurrentInstance, ref } from 'vue';
  import { useUi } from '@/composition/use-ui';
  import { AlertType } from '@/interfaces/IAlert';
  import { useV2PrintingUi } from '@/composition/printing/use-printing-v2-ui';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import V2PrintingTabs, { IV2PrintingTab } from './V2PrintingTabs.vue';
  import V2PrintingPrintersTab from './V2PrintingPrintersTab.vue';
  import V2PrintingJobsTab from './V2PrintingJobsTab.vue';
  import V2PrintingPreferencesTab from './V2PrintingPreferencesTab.vue';
  import V2PrintingHelpTab, { IV2HelpFlow } from './V2PrintingHelpTab.vue';

  const props = defineProps<{
    /** Deep-link: tab to open on (widget "Ver impresiones" → 'jobs'). */
    initialTab?: string;
  }>();

  const emit = defineEmits<{
    (e: 'close'): void;
  }>();

  const ui = useUi();
  const { bridge, isConnected, v2Store } = useV2PrintingUi();

  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  const VALID_TABS = ['printers', 'jobs', 'preferences', 'help'];
  const activeTab = ref(
    props.initialTab && VALID_TABS.includes(props.initialTab)
      ? props.initialTab
      : 'printers'
  );

  /** Flow to auto-open when Help is entered via a shortcut (offline hint). */
  const helpInitialFlow = ref<IV2HelpFlow | null>(null);

  function selectTab(tab: string) {
    helpInitialFlow.value = null;
    activeTab.value = tab;
  }

  function openHelp(flow: IV2HelpFlow) {
    helpInitialFlow.value = flow;
    activeTab.value = 'help';
  }

  const identity = computed(() => bridge.value?.identity.value ?? null);

  const failedJobsCount = computed(
    () =>
      (bridge.value?.jobs.value ?? []).filter(
        (job) => job.state === 'failed' || job.state === 'unknown'
      ).length
  );

  const tabs = computed<IV2PrintingTab[]>(() => [
    {
      value: 'printers',
      label: i18nT('printingV2.tabPrinters'),
      icon: 'printer',
      count: v2Store.unresolvedV1.length,
    },
    {
      value: 'jobs',
      label: i18nT('printingV2.tabJobs'),
      icon: 'history',
      count: failedJobsCount.value,
    },
    {
      value: 'preferences',
      label: i18nT('printingV2.tabPreferences'),
      icon: 'tune',
      count: 0,
    },
    {
      value: 'help',
      label: i18nT('printingV2.tabHelp'),
      icon: 'lifebuoy',
      count: 0,
    },
  ]);

  // #region instance rename (inline, header pencil)

  const renaming = ref(false);
  const renameValue = ref('');
  const renameSaving = ref(false);

  function startRename() {
    renameValue.value = identity.value?.instanceName ?? '';
    renaming.value = true;
  }

  async function saveRename() {
    const name = renameValue.value.trim();
    if (!name || !bridge.value) {
      renaming.value = false;
      return;
    }
    renameSaving.value = true;
    try {
      await bridge.value.setInstanceName(name);
      renaming.value = false;
    } catch (e) {
      ui.showToast(AlertType.Error, i18nT('printingV2.renameFailed'));
    } finally {
      renameSaving.value = false;
    }
  }

  // #endregion
</script>

<template>
  <div class="v2-printing-modal">
    <div class="v2-printing-modal-header">
      <div class="header-main">
        <h2 class="header-title">{{ $t('printingV2.title') }}</h2>

        <template v-if="isConnected && identity">
          <div v-if="renaming" class="header-rename">
            <PosUiInput
              :value="renameValue"
              size="sm"
              :maxlength="40"
              class="header-rename-input"
              @input="renameValue = $event"
              @enter="saveRename"
            />
            <PosUiButton
              size="xs"
              color="primary"
              icon="check"
              :loading="renameSaving"
              @click="saveRename"
            />
            <PosUiButton
              size="xs"
              color="neutral"
              variant="ghost"
              icon="close"
              :disabled="renameSaving"
              @click="renaming = false"
            />
          </div>
          <PosUiBadge v-else color="success" variant="soft" size="md">
            <span class="header-status-dot connected" />
            {{
              $t('printingV2.connectedChip', {
                name: identity.instanceName,
                version: identity.version,
              })
            }}
            <button
              class="header-rename-pencil"
              type="button"
              :title="$t('printingV2.rename')"
              @click="startRename"
            >
              <PosUiIcon name="pencil" />
            </button>
          </PosUiBadge>
        </template>
        <PosUiBadge v-else color="neutral" variant="soft" size="md">
          <span class="header-status-dot" />
          {{ $t('printingV2.disconnected') }}
        </PosUiBadge>
      </div>

      <button
        class="header-close"
        type="button"
        :title="$t('printingV2.close')"
        @click="emit('close')"
      >
        <PosUiIcon name="close" :size="22" />
      </button>
    </div>

    <div class="v2-printing-modal-tabs">
      <V2PrintingTabs :value="activeTab" :tabs="tabs" @input="selectTab" />
    </div>

    <div class="v2-printing-modal-body">
      <V2PrintingPrintersTab
        v-if="activeTab === 'printers'"
        @open-help="openHelp"
      />
      <V2PrintingJobsTab v-else-if="activeTab === 'jobs'" />
      <V2PrintingPreferencesTab v-else-if="activeTab === 'preferences'" />
      <V2PrintingHelpTab
        v-else-if="activeTab === 'help'"
        :initial-flow="helpInitialFlow"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
  .v2-printing-modal {
    @apply tw-flex tw-max-h-[85vh] tw-min-h-[480px] tw-flex-col tw-bg-white;

    .v2-printing-modal-header {
      @apply tw-flex tw-items-center tw-justify-between tw-gap-3 tw-border-b tw-border-neutral-100 tw-px-5 tw-py-4;

      .header-main {
        @apply tw-flex tw-min-w-0 tw-flex-wrap tw-items-center tw-gap-3;

        .header-title {
          @apply tw-m-0 tw-text-lg tw-font-bold tw-text-neutral-900;
        }

        .header-status-dot {
          // Spaced from the badge text by a margin, not the badge's flex gap
          // (old POS hardware, Chrome < 84).
          @apply tw-mr-1 tw-h-2 tw-w-2 tw-rounded-full tw-bg-neutral-400;

          &.connected {
            @apply tw-bg-emerald-500;
          }
        }

        .header-rename {
          @apply tw-flex tw-items-center tw-gap-1;

          .header-rename-input {
            @apply tw-w-52;
          }
        }

        .header-rename-pencil {
          // The badge's old 4px gap plus its own 2px.
          @apply tw-ml-1.5 tw-flex tw-items-center tw-text-emerald-600 hover:tw-text-emerald-800;
        }
      }

      .header-close {
        @apply tw-flex tw-h-9 tw-w-9 tw-flex-none tw-items-center tw-justify-center tw-rounded-lg tw-text-neutral-400 tw-transition-colors hover:tw-bg-neutral-100 hover:tw-text-neutral-700;
      }
    }

    .v2-printing-modal-tabs {
      @apply tw-px-5 tw-pt-4;
    }

    .v2-printing-modal-body {
      @apply tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-px-5 tw-py-4;
    }
  }
</style>
